import React, { useCallback, useRef, useEffect } from 'react'
import { useQuery } from '@apollo/client/react'
import { Button } from '@mantine/core'
import PublicRooms from '../graphql/queries/PublicRooms'
import Spinner from '../ui/Spinner'

export default function PublicRoomsFeed({ height, onSelectRoom, joiningRoomId = null }) {
  const listRef = useRef(null)
  const { data, loading, fetchMore } = useQuery(PublicRooms, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  })

  const rooms = data?.publicRooms?.rooms ?? []
  const cursor = data?.publicRooms?.cursor
  const RECENT_WINDOW_MS = 5 * 60 * 1000

  // If no height is specified, automatically fetch all rooms
  useEffect(() => {
    if (!height && cursor && !loading) {
      fetchMore({ variables: { cursor } })
    }
  }, [height, cursor, loading, fetchMore])

  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    const atBottom = scrollTop + clientHeight >= scrollHeight - 8
    if (atBottom && cursor && !loading) {
      fetchMore({ variables: { cursor } })
    }
  }, [cursor, fetchMore, loading])

  return (
    <div
      ref={listRef}
      onScroll={height ? handleScroll : undefined}
      style={{
        ...(height ? { maxHeight: height, overflowY: 'auto' } : {}),
        borderRadius: 8,
        border: '1px solid rgba(255,255,255,0.08)'
      }}
      className="PublicRoomsFeed"
    >
      { rooms.length === 0 && loading ? (
        <div style={{ padding: 16, display: 'flex', justifyContent: 'center' }}><Spinner /></div>
      ) : (
        rooms.map((room, index) => {
          const nowTs = Date.now()
          const activeCount = room.members?.filter(m => (
            typeof m.lastActivity === 'number' && (nowTs - m.lastActivity) <= RECENT_WINDOW_MS
          )).length ?? 0
          const nowTrack = room.queue?.[0]?.track
          const spinningText = (room.spinning?.trim()) ? room.spinning : 'random'
          const topic = room.topic
          const baseBg = index % 2 === 0 ? '#23262b' : '#262a31'
          const gradient = 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.00) 100%)'
          const rowDisabled = !!joiningRoomId || !onSelectRoom
          const buttonLoading = joiningRoomId === room.id
          return (
            <div
              key={room.id}
              className="PublicRoomItem"
              onClick={() => { if (!rowDisabled) onSelectRoom?.(room.id) }}
              style={{
                padding: '12px 14px',
                cursor: rowDisabled ? 'not-allowed' : 'pointer',
                opacity: rowDisabled ? 0.8 : 1,
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                backgroundColor: baseBg,
                backgroundImage: gradient
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{room.name}</div>
                  { topic && topic.trim() ? (
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{topic}</div>
                  ) : null }
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.7)' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#a5b4fc' }}>groups</span>
                    <span style={{ fontSize: 12 }}>{activeCount}</span>
                  </div>
                  <Button
                    size="xs"
                    radius="sm"
                    variant="light"
                    color="indigo"
                    disabled={!!joiningRoomId}
                    loading={buttonLoading}
                    onClick={(e) => { e.stopPropagation(); onSelectRoom?.(room.id) }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 6 }}>play_arrow</span>
                    Join
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 6, display: 'grid', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.8)' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#a5b4fc' }}>sell</span>
                  <span style={{ fontSize: 12 }}>{ spinningText }</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.8)' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#a5b4fc' }}>music_note</span>
                  <span style={{ fontSize: 12 }}>
                    { nowTrack ? (<>{nowTrack.title} — <span style={{ opacity: 0.9 }}>{nowTrack.artist}</span></>) : 'Nothing playing' }
                  </span>
                </div>
              </div>
            </div>
          )
        })
      )}
      { loading && rooms.length > 0 ? (
        <div style={{ padding: 12, display: 'flex', justifyContent: 'center' }}><Spinner /></div>
      ) : null }
    </div>
  )
}
